import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Link2 } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ContactPicker from "./ContactPicker";
import { crmErr, money, when } from "./helpers";

function Orphan({ tid, d, onLinked }) {
  const api = useApi();
  const [who, setWho] = useState(null);
  const link = async () => {
    try { await api.post(`/t/${tid}/crm/deals/${d.id}/link`, { contact_id: who.id }); toast.success(`«${d.title}» associata a ${who.label}`); onLinked(); }
    catch (e) { toast.error(crmErr(e, errMsg)); }
  };
  return (
    <li className="space-y-2 rounded-lg border p-3 text-xs" data-testid={`orphan-deal-${d.id}`}>
      <p><b className="text-slate-800">{d.title}</b> · {d.stage_name} · valore {money(d.value_cents)} · revenue {money(d.money.revenue)} · incassato {money(d.money.cash)} · creata {when(d.created_at)}</p>
      <ContactPicker tid={tid} value={who} onChange={setWho} testid={`orphan-contact-${d.id}`} />
      <div className="flex justify-end"><Button size="sm" onClick={link} disabled={!who} data-testid={`orphan-link-${d.id}`}><Link2 size={13} className="mr-1" />Associa a contatto</Button></div>
    </li>);
}

// Recovery of opportunities created earlier without a contact: they keep amounts, stage and history; here they get a contact.
export default function OrphanDeals({ tid, canWrite, tick, onChanged }) {
  const api = useApi();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get(`/t/${tid}/crm/deals/orphans`).then(({ data }) => { setList(data); if (!data.length) setOpen(false); }).catch(() => setList([])), [api, tid]);
  useEffect(() => { load(); }, [load, tick]);
  if (!list.length) return null;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="orphan-deals-banner">
        <AlertTriangle size={14} className="shrink-0" />
        <span className="flex-1">{list.length === 1 ? "1 trattativa non ha un contatto" : `${list.length} trattative non hanno un contatto`}: sono incluse in Pipeline e Indicatori ma non compaiono nella Tabella. Associale a un contatto per ritrovarle.</span>
        {canWrite && <Button size="sm" variant="outline" className="h-7 bg-white" onClick={() => setOpen(true)} data-testid="orphan-deals-open">Associa a contatto</Button>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="orphan-deals-dialog">
          <DialogHeader><DialogTitle>Trattative senza contatto</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Importi, stato e storico restano invariati: scegli o crea il contatto e premi «Associa».</p>
          <ul className="space-y-2">{list.map((d) => <Orphan key={d.id} tid={tid} d={d} onLinked={() => { load(); onChanged?.(); }} />)}</ul>
        </DialogContent>
      </Dialog>
    </>);
}
